import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  CircuitBoard,
  FileText,
  FileUp,
  Link as LinkIcon,
  Trash2,
  Users,
  Waves,
} from "lucide-react";
import type { MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { EmptyState } from "../../components/app/EmptyState";
import { Metric } from "../../components/app/Metric";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { formatDateRange, formatShortDate } from "../../lib/baseline/format";
import { cn } from "../../lib/utils";
import { useCurrentCompany, useCurrentUser, useSession } from "../../services/SessionContext";

function ClickableDashboardCard({
  to,
  title,
  icon,
  children,
  className,
}: {
  to: string;
  title: string;
  icon: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        "group block h-full rounded-lg border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-water-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-water-400 focus:ring-offset-2",
        className,
      )}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-water-50 text-water-600 ring-1 ring-water-100">
            {icon}
          </span>
          <CardTitle>{title}</CardTitle>
        </div>
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 transition group-hover:bg-water-50 group-hover:text-water-600">
          <ArrowRight className="h-4 w-4" aria-hidden />
        </span>
      </CardHeader>
      {children}
    </Link>
  );
}

export function DashboardPage() {
  const { state, repo, deleteUpload } = useSession();
  const company = useCurrentCompany();
  const user = useCurrentUser();
  if (!company || !user) return null;

  const uploads = repo.getCompanyUploads(company.companyId);
  const members = repo.getMembers(company.companyId);
  const links = state.shareLinks.filter((link) => link.companyId === company.companyId);
  const activeMember = repo.activeMember(company.companyId, user.userId);
  const canUpload = activeMember?.role !== "VIEWER";
  const totals = uploads.reduce(
    (acc, upload) => ({
      events: acc.events + upload.rowCount,
      critical: acc.critical + upload.criticalIssueCount,
      warnings: acc.warnings + upload.warningCount,
      watering: acc.watering + upload.wateringEventCount,
    }),
    { events: 0, critical: 0, warnings: 0, watering: 0 },
  );
  const processedUploadCount = uploads.filter((u) => u.status === "PROCESSED").length;

  function confirmDeleteUpload(event: MouseEvent<HTMLButtonElement>, uploadId: string, uploadName: string) {
    event.preventDefault();
    event.stopPropagation();
    const confirmed = window.confirm(
      `Delete "${uploadName}"? This removes the upload, its events, and any share links created from it.`,
    );
    if (confirmed) deleteUpload(uploadId);
  }

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-950">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-600">{company.name} event file repository</p>
        </div>
        {canUpload ? (
          <Button asChild>
            <Link to="/app/uploads/new">
              <FileUp className="h-4 w-4" aria-hidden /> Upload event file
            </Link>
          </Button>
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        {canUpload ? (
          <ClickableDashboardCard
            to="/app/uploads/new"
            title="Upload event file"
            icon={<FileUp className="h-5 w-5" aria-hidden />}
          >
            <CardContent>
              <p className="text-sm text-slate-600">
                Look for a file named Evnt_{"{date}"}.csv inside the controller export folder on
                your USB drive.
              </p>
              <p className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-navy-900">
                Upload & Process <ArrowRight className="h-4 w-4" aria-hidden />
              </p>
            </CardContent>
          </ClickableDashboardCard>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Upload event file</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                Look for a file named Evnt_{"{date}"}.csv inside the controller export folder on
                your USB drive.
              </p>
            </CardContent>
          </Card>
        )}
        <ClickableDashboardCard
          to="/app/repository"
          title="Company repository summary"
          icon={<FileText className="h-5 w-5" aria-hidden />}
        >
          <CardContent className="grid grid-cols-2 gap-3">
            <Metric icon={<FileText className="h-4 w-4" />} label="Uploads" value={uploads.length} />
            <Metric icon={<Waves className="h-4 w-4" />} label="Events" value={totals.events.toLocaleString()} tone="water" />
            <Metric label="Critical" value={totals.critical} tone="critical" />
            <Metric label="Warnings" value={totals.warnings} tone="warning" />
          </CardContent>
        </ClickableDashboardCard>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <ClickableDashboardCard
          to="/app/repository"
          title="Saved reports"
          icon={<FileText className="h-5 w-5" aria-hidden />}
        >
          <CardContent>
            <p className="text-3xl font-bold text-navy-950">{processedUploadCount}</p>
            <p className="mt-1 text-sm text-slate-600">Processed event histories</p>
          </CardContent>
        </ClickableDashboardCard>
        <ClickableDashboardCard
          to="/app/shared"
          title="Shared links"
          icon={<LinkIcon className="h-5 w-5" aria-hidden />}
        >
          <CardContent>
            <p className="text-3xl font-bold text-navy-950">{links.filter((link) => link.isActive).length}</p>
            <p className="mt-1 text-sm text-slate-600">Active public, client, and support links</p>
          </CardContent>
        </ClickableDashboardCard>
        <ClickableDashboardCard
          to="/app/team"
          title="Team members"
          icon={<Users className="h-5 w-5" aria-hidden />}
        >
          <CardContent>
            <p className="text-3xl font-bold text-navy-950">{members.length}</p>
            <p className="mt-1 text-sm text-slate-600">Users in this workspace</p>
          </CardContent>
        </ClickableDashboardCard>
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="border-b border-slate-100 bg-slate-50/80">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Recent uploads</CardTitle>
              <p className="mt-1 text-sm text-slate-600">Recently processed controller files</p>
            </div>
            <Button asChild variant="secondary" size="sm">
              <Link to="/app/repository">
                Repository <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-5">
          {uploads.length === 0 ? (
            <EmptyState
              title="Upload your first Baseline event file."
              action={
                canUpload ? (
                  <Button asChild>
                    <Link to="/app/uploads/new">Upload event file</Link>
                  </Button>
                ) : null
              }
            />
          ) : (
            <ul className="space-y-3">
              {uploads.slice(0, 8).map((upload) => {
                const uploadName = upload.projectName || upload.controllerName || upload.originalFileName;
                const secondaryName =
                  upload.controllerName && upload.controllerName !== uploadName
                    ? upload.controllerName
                    : upload.originalFileName;
                const issueCount = upload.criticalIssueCount + upload.warningCount;
                const statusClass =
                  upload.status === "FAILED"
                    ? "bg-red-50 text-red-700 ring-red-200"
                    : upload.status === "PROCESSING"
                      ? "bg-amber-50 text-amber-800 ring-amber-200"
                      : "bg-field-100 text-field-600 ring-field-600/20";

                return (
                  <li key={upload.uploadId}>
                    <article className="group relative cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-water-300 hover:shadow-lg focus-within:ring-2 focus-within:ring-water-400 focus-within:ring-offset-2">
                      <Link
                        to={`/app/uploads/${upload.uploadId}`}
                        className="absolute inset-0 z-0 rounded-xl"
                        aria-label={`Open report for ${uploadName}`}
                      >
                        <span className="sr-only">Open report for {uploadName}</span>
                      </Link>
                      <div className="pointer-events-none relative z-10 grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                        <div className="flex min-w-0 gap-4">
                          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-water-50 text-water-700 ring-1 ring-water-100 transition group-hover:bg-water-600 group-hover:text-white">
                            <CircuitBoard className="h-6 w-6" aria-hidden />
                          </span>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="truncate text-lg font-semibold text-navy-950 group-hover:underline">
                                {uploadName}
                              </h3>
                              <Badge className={statusClass}>{upload.status}</Badge>
                            </div>
                            <p className="mt-1 text-sm text-slate-600">{secondaryName}</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <span className="inline-flex h-8 items-center gap-1.5 rounded-md bg-slate-50 px-2.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
                                <FileText className="h-3.5 w-3.5 text-slate-500" aria-hidden />
                                {upload.rowCount.toLocaleString()} events
                              </span>
                              <span
                                className={cn(
                                  "inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold ring-1",
                                  issueCount > 0
                                    ? "bg-amber-50 text-amber-800 ring-amber-200"
                                    : "bg-field-100 text-field-700 ring-field-600/20",
                                )}
                              >
                                <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                                {issueCount} issues
                              </span>
                              <span className="inline-flex h-8 items-center gap-1.5 rounded-md bg-water-50 px-2.5 text-xs font-semibold text-water-700 ring-1 ring-water-100">
                                <Waves className="h-3.5 w-3.5" aria-hidden />
                                {upload.wateringEventCount.toLocaleString()} watering
                              </span>
                              <span className="inline-flex h-8 items-center gap-1.5 rounded-md bg-slate-50 px-2.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
                                <CalendarDays className="h-3.5 w-3.5 text-slate-500" aria-hidden />
                                {formatDateRange(upload.dateRangeStart, upload.dateRangeEnd)}
                              </span>
                            </div>
                            <p className="mt-3 text-xs font-medium text-slate-500">
                              Uploaded {formatShortDate(upload.createdAt)}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                          <span className="inline-flex h-9 items-center gap-2 rounded-md bg-navy-900 px-3 text-sm font-semibold text-white shadow-sm transition group-hover:bg-water-600">
                            Open report <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden />
                          </span>
                          {canUpload ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="pointer-events-auto relative z-20 text-slate-500 hover:bg-red-50 hover:text-red-700"
                              onClick={(event) => confirmDeleteUpload(event, upload.uploadId, uploadName)}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden /> Delete
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </article>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <ClickableDashboardCard
          to="/app/shared"
          title="Shared links"
          icon={<LinkIcon className="h-5 w-5" aria-hidden />}
        >
          <CardContent className="space-y-3">
            {links.length === 0 ? <p className="text-sm text-slate-600">No share links created yet.</p> : null}
            {links.slice(0, 4).map((link) => {
              const upload = uploads.find((item) => item.uploadId === link.uploadId);
              return (
                <div key={link.shareId} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 p-3">
                  <div>
                    <p className="text-sm font-semibold text-navy-900">{upload?.projectName || upload?.originalFileName}</p>
                    <p className="text-xs text-slate-500">{link.type.replace("_", " ")} link</p>
                  </div>
                  <Badge className={link.isActive ? "bg-field-100 text-field-600 ring-field-600/20" : "bg-slate-100 text-slate-600 ring-slate-200"}>
                    {link.isActive ? "Active" : "Disabled"}
                  </Badge>
                </div>
              );
            })}
          </CardContent>
        </ClickableDashboardCard>
        <ClickableDashboardCard
          to="/app/team"
          title="Team members"
          icon={<Users className="h-5 w-5" aria-hidden />}
        >
          <CardContent className="space-y-3">
            {repo.getMemberUsers(company.companyId).slice(0, 5).map(({ member, user: memberUser }) => (
              <div key={member.userId} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 p-3">
                <div>
                  <p className="text-sm font-semibold text-navy-900">{memberUser?.name || memberUser?.email || member.invitedEmail}</p>
                  <p className="text-xs text-slate-500">{memberUser?.email || member.invitedEmail}</p>
                </div>
                <Badge className="bg-slate-50 text-slate-700 ring-slate-200">{member.role}</Badge>
              </div>
            ))}
          </CardContent>
        </ClickableDashboardCard>
      </div>
    </div>
  );
}
